import { useEffect } from 'react'

import { joiResolver } from '@hookform/resolvers/joi'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { LoadingButton } from '@mui/lab'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { FplDialogTitle } from 'components/common'
import { ControlledTextField, ControlledSelector } from 'components/common/controller-wrapper'
import { JoiValidationOptions } from 'constants/index'
import {
  LegalDocumentType,
  useCreateLegalDocumentMutation,
  useGetLegalDocumentQuery,
} from 'generated/graphql'
import { isNumber, SaveFile } from 'helpers'
import { DownloadLegalDocumentFile } from 'services/RestClient'
import {
  LegalDocumentFormValues,
  LegalDocumentValidation,
} from 'validation-schemas/LegalDocument.validation'

import { DOCUMENT_TYPE_OPTIONS, DOCUMENT_TYPE_LABELS } from './constants'
import { DocxUpload } from './DocxUpload'
import { useLatestVersion } from './hooks/useLatestVersion'
import { LegalDocumentPreview } from './LegalDocumentPreview'

const handleDownloadFile = (uri: string, fileName: string) => {
  DownloadLegalDocumentFile(uri, fileName)
    .then((response) => SaveFile(response.data, fileName))
    .catch(() => toast.error('Failed to download file'))
}

interface IProps {
  id: number | null
  openDialog: boolean
  onCloseDialog: () => void
}

const CreateLegalDocumentForm = ({
  openDialog,
  onCloseDialog,
}: {
  openDialog: boolean
  onCloseDialog: () => void
}) => {
  const { getNextVersion } = useLatestVersion()

  const methods = useForm<LegalDocumentFormValues>({
    resolver: joiResolver(LegalDocumentValidation, JoiValidationOptions),
    defaultValues: {
      name: '',
      versionMajor: 1,
      versionMinor: 0,
      versionPatch: 0,
      documentType: LegalDocumentType.TermsAndConditions,
      content: '',
      originalFileUri: '',
      originalFileName: '',
    },
  })

  const { handleSubmit, setValue, watch } = methods

  const content = watch('content')
  const originalFileName = watch('originalFileName')
  const originalFileUri = watch('originalFileUri')
  const documentType = watch('documentType')

  useEffect(() => {
    const populateVersion = async () => {
      const nextVersion = await getNextVersion(documentType as LegalDocumentType)
      setValue('versionMajor', nextVersion.major)
      setValue('versionMinor', nextVersion.minor)
      setValue('versionPatch', nextVersion.patch)
    }

    populateVersion()
  }, [documentType, getNextVersion, setValue])

  const [createLegalDocument, { loading: creating }] = useCreateLegalDocumentMutation({
    refetchQueries: ['GetLegalDocuments', 'GetLegalDocumentsAll'],
  })

  const onSubmit: SubmitHandler<LegalDocumentFormValues> = async (formData) => {
    try {
      await createLegalDocument({
        variables: {
          input: {
            name: formData.name,
            versionMajor: Number(formData.versionMajor),
            versionMinor: Number(formData.versionMinor),
            versionPatch: Number(formData.versionPatch),
            documentType: formData.documentType as LegalDocumentType,
            content: formData.content,
            originalFileUri: formData.originalFileUri || null,
            originalFileName: formData.originalFileName || null,
          },
        },
      })
      toast.success(
        'Legal document created successfully! Previous active version has been deactivated.',
      )
      onCloseDialog()
    } catch (error: any) {
      toast.error(error?.message || 'An error occurred')
    }
  }

  const handleDocxParsed = (parsedContent: string, fileUri: string, fileName: string) => {
    setValue('content', parsedContent, { shouldValidate: true })
    setValue('originalFileUri', fileUri)
    setValue('originalFileName', fileName)
  }

  const handleFileNameResolved = (fileName: string) => {
    setValue('name', fileName)
  }

  return (
    <Dialog open={openDialog} onClose={onCloseDialog} maxWidth='lg' fullWidth>
      <FplDialogTitle onClose={onCloseDialog}>Create New Legal Document Version</FplDialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <ControlledTextField
                  name='name'
                  label='Document Name'
                  control={methods.control}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Stack direction='row' spacing={1}>
                  <Controller
                    name='versionMajor'
                    control={methods.control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label='Major'
                        type='number'
                        fullWidth
                        required
                        inputProps={{ min: 0 }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        onChange={(e) => {
                          field.onChange(e.target.value)
                          setValue('versionMinor', 0)
                          setValue('versionPatch', 0)
                        }}
                      />
                    )}
                  />
                  <Controller
                    name='versionMinor'
                    control={methods.control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        label='Minor'
                        type='number'
                        fullWidth
                        required
                        inputProps={{ min: 0 }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        onChange={(e) => {
                          field.onChange(e.target.value)
                          setValue('versionPatch', 0)
                        }}
                      />
                    )}
                  />
                  <ControlledTextField
                    name='versionPatch'
                    label='Patch'
                    control={methods.control}
                    required
                    type='number'
                    min='0'
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} sm={3}>
                <ControlledSelector
                  name='documentType'
                  label='Document Type'
                  control={methods.control}
                  options={DOCUMENT_TYPE_OPTIONS.map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                  }))}
                />
              </Grid>

              <Grid item xs={12}>
                <DocxUpload
                  onDocxParsed={handleDocxParsed}
                  onFileNameResolved={handleFileNameResolved}
                />
              </Grid>

              {originalFileName && (
                <Grid item xs={12}>
                  <Typography variant='body2' color='text.secondary'>
                    Original file: {originalFileName}
                    {originalFileUri && (
                      <>
                        {' — '}
                        <a
                          href='#'
                          onClick={(e) => {
                            e.preventDefault()
                            handleDownloadFile(originalFileUri, originalFileName)
                          }}>
                          Download
                        </a>
                      </>
                    )}
                  </Typography>
                </Grid>
              )}

              {content && (
                <Grid item xs={12}>
                  <Accordion variant='outlined'>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant='subtitle2'>Document Content Preview</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          maxHeight: 400,
                          overflow: 'auto',
                          lineHeight: 1.8,
                          fontSize: '15px',
                        }}>
                        <LegalDocumentPreview content={content} />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={onCloseDialog}>Cancel</Button>
            <LoadingButton
              type='submit'
              variant='contained'
              loading={creating}
              disabled={creating}>
              Create
            </LoadingButton>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}

export const LegalDocumentDialog = ({ id, openDialog, onCloseDialog }: IProps) => {
  const isViewMode = id !== null && isNumber(id)

  const { data: existingData, loading: loadingDocument } = useGetLegalDocumentQuery({
    variables: { id: id! },
    skip: !isViewMode,
  })

  const viewDoc = existingData?.legalDocument

  if (isViewMode) {
    return (
      <Dialog open={openDialog} onClose={onCloseDialog} maxWidth='lg' fullWidth>
        <FplDialogTitle onClose={onCloseDialog}>Legal Document</FplDialogTitle>
        <DialogContent dividers>
          {loadingDocument ? (
            <Typography>Loading…</Typography>
          ) : viewDoc ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant='caption' color='text.secondary'>
                  Document Name
                </Typography>
                <Typography>{viewDoc.name}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant='caption' color='text.secondary'>
                  Version
                </Typography>
                <Typography>{viewDoc.versionMajor}.{viewDoc.versionMinor}.{viewDoc.versionPatch}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                    Type
                    </Typography>

                    <Chip
                    label={
                        DOCUMENT_TYPE_LABELS[viewDoc.documentType as string] ||
                        viewDoc.documentType
                    }
                    size="small"
                    color="primary"
                    variant="outlined"
                    />
                </Stack>
              </Grid>

              {viewDoc.originalFileName && (
                <Grid item xs={12}>
                  <Typography variant='body2' color='text.secondary'>
                    Original file: {viewDoc.originalFileName}
                    {viewDoc.originalFileUri && (
                      <>
                        {' — '}
                        <a
                          href='#'
                          onClick={(e) => {
                            e.preventDefault()
                            handleDownloadFile(viewDoc.originalFileUri!, viewDoc.originalFileName!)
                          }}>
                          Download
                        </a>
                      </>
                    )}
                  </Typography>
                </Grid>
              )}

              {viewDoc.content && (
                <Grid item xs={12}>
                  <Accordion variant='outlined' defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant='subtitle2'>Document Content</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box
                        sx={{
                          maxHeight: 500,
                          overflow: 'auto',
                          lineHeight: 1.8,
                          fontSize: '15px',
                        }}>
                        <LegalDocumentPreview content={viewDoc.content} />
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              )}
            </Grid>
          ) : (
            <Typography>Document not found.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    )
  }

  return <CreateLegalDocumentForm openDialog={openDialog} onCloseDialog={onCloseDialog} />
}
